import { Component, lazy, Suspense, useEffect, useState } from "react";
import { CONTACT } from "@/constants/contact";

// The booking form (+ its date calendar) is its own download — fetched on the first Book
// tap, or a head start when a Book button is hovered / touched / focused.
let chunk = null;
export const preloadBookingDialog = () => {
  if (!chunk) chunk = import("@/components/BookingDialog").catch((e) => { chunk = null; throw e; });
  return chunk;
};
// React.lazy remembers a failed load, so "Try again" swaps in a fresh one.
const makeLazy = () => lazy(() => preloadBookingDialog().then((m) => ({ default: m.BookingDialog })));
let BookingDialog = makeLazy();

// Put on the page root: any element marked data-book-cta starts the download early.
const warm = (e) => {
  if (e.target.closest?.("[data-book-cta]")) preloadBookingDialog().catch(() => {});
};
export const bookingPreloadHandlers = { onPointerOverCapture: warm, onTouchStartCapture: warm, onFocusCapture: warm };

const Shell = ({ onClose, children, testid, tall = false }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div data-testid={testid} className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        className={`w-full sm:max-w-[560px] bg-background p-6 sm:rounded-lg shadow-lg ${tall ? "max-sm:h-[100dvh]" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

const Bar = ({ className }) => <div className={`rounded bg-muted animate-pulse ${className}`} />;

const BookingLoading = ({ onClose }) => (
  <Shell onClose={onClose} testid="booking-dialog-loading" tall>
    <div aria-busy="true" aria-label="Loading booking form" className="space-y-4">
      <Bar className="h-7 w-2/3" />
      <Bar className="h-4 w-full" />
      <Bar className="h-10 w-full" />
      <Bar className="h-56 w-full" />
      <Bar className="h-10 w-full" />
    </div>
  </Shell>
);

class LoadFailed extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (!this.state.failed) return this.props.children;
    if (!this.props.open) return null;
    return (
      <Shell onClose={this.props.onClose} testid="booking-dialog-load-error">
        <p className="font-display text-xl font-bold text-navy">The booking form didn't load</p>
        <p className="text-sm text-muted-foreground mt-2">Check your connection and try again — or email {CONTACT.email} and I'll book you in myself.</p>
        <div className="mt-5 flex gap-3">
          <button data-testid="booking-dialog-retry" onClick={this.props.onRetry} className="bg-terracotta hover:bg-terracotta-hover text-white font-semibold px-5 h-11 rounded-md transition-colors">Try again</button>
          <button data-testid="booking-dialog-load-error-close" onClick={this.props.onClose} className="px-5 h-11 font-semibold text-navy hover:text-terracotta transition-colors">Close</button>
        </div>
      </Shell>
    );
  }
}

export const LazyBookingDialog = (props) => {
  const [mounted, setMounted] = useState(false);
  const [attempt, setAttempt] = useState(0);
  if (props.open && !mounted) setMounted(true);
  if (!mounted) return null;
  const close = () => props.onOpenChange(false);
  const retry = () => {
    BookingDialog = makeLazy();
    setAttempt((a) => a + 1);
  };
  return (
    <LoadFailed key={attempt} open={props.open} onClose={close} onRetry={retry}>
      <Suspense fallback={props.open ? <BookingLoading onClose={close} /> : null}>
        <BookingDialog {...props} />
      </Suspense>
    </LoadFailed>
  );
};
